<template>
  <div class="trendline-chart-container" data-testid="commentary-trendline-tab">
    <canvas :id="trendchartElementId" style="max-height: 400px" data-testid="commentary-trendline-tab__chart"></canvas>
    <div
      v-if="isCurrentLoading"
      class="trendline-chart-container__loader"
      data-testid="commentary-trendline-tab__loader"
    >
      <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
    </div>
    <div
      v-else-if="!hasData && !currentError"
      class="trendline-chart-container__empty"
      data-testid="commentary-trendline-tab__empty-state"
    >
      No trendline data available.
    </div>
    <div
      v-else-if="currentError"
      class="trendline-chart-container__error error--text"
      data-testid="commentary-trendline-tab__error-state"
    >
      {{ currentError }}
    </div>
  </div>
</template>

<script>
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { mapActions, mapGetters } from 'vuex';

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
);

export default {
  name: 'CommentaryTrendlineTab',
  props: {
    bulksubmit: { type: Boolean, default: false },
    charttab: { type: Number, default: 0 },
    cycletype: { type: String, default: '' },
    reportingType: { type: String, default: '' },
    trendYear: { type: [String, Number], default: '' },
    trendPeriod: { type: [String, Number], default: '' },
    trendReportingUnit: { type: [String, Number], default: '' },
    glContext: { type: Object, default: () => ({}) },
    selectedcommentary: { type: Object, default: () => ({}) },
    toggleview: { type: Number, default: 0 },
    actualLabel: { type: String, default: 'Actual' },
    proformaLabel: { type: String, default: 'Proforma' },
    forecastLabel: { type: String, default: 'Forecast' },
  },
  data() {
    return {
      trendchart: null,
      trendchartElementId: `trendchart-${this._uid || Math.random().toString(36).slice(2)}`,
      isMounted: false,
      primaryHeader: '',
      isFetchingData: false, // Guard flag to prevent concurrent fetches
      isFetchingLogs: false,
      pendingDataRefetch: false, // Flag to retry after current fetch completes
      pendingLogsRefetch: false,
    };
  },
  computed: {
    ...mapGetters('varianceanalysis', {
      trendlineDataGetter: 'getTrendlineData',
      trendlineLoadingFromStore: 'getTrendlineLoading',
      trendlineActiveKey: 'getTrendlineActiveKey',
      trendlineErrorGetter: 'getTrendlineError',
      trendlineLogsGetter: 'getTrendlineLogs',
      trendlineLogsLoadingFlag: 'getTrendlineLogsLoading',
      trendlineLogsActiveKey: 'getTrendlineLogsActiveId',
      trendlineLogsErrorGetter: 'getTrendlineLogsError',
    }),
    resolvedContext() {
      const base = this.selectedcommentary || {};
      const sanitized = this.glContext && Object.keys(this.glContext).length > 0 ? this.glContext : {};
      return { ...base, ...sanitized };
    },
    isActiveTab() {
      return !this.bulksubmit && this.charttab === 1;
    },
    currentCycleType() {
      const source = typeof this.cycletype === 'string' ? this.cycletype.trim() : '';
      if (!source) {
        return 'Actual';
      }

      const normalized = source.toLowerCase();
      if (normalized === 'actual') {
        return 'Actual';
      }
      if (normalized === 'forecast') {
        return 'Forecast';
      }
      if (normalized === 'proforma') {
        return 'Proforma';
      }

      return source;
    },

    contextReportingTypeKey() {
      const propValue = typeof this.reportingType === 'string' ? this.reportingType.trim() : '';
      const contextValue =
        this.resolvedContext?.glcode_detail?.reporting_type ||
        this.resolvedContext?.reporting_type ||
        '';
      const candidate = (propValue || contextValue || '').toString().trim().toLowerCase();

      if (candidate === 'bs' || candidate === 'balance_sheet' || candidate === 'balance sheet') {
        return 'bs';
      }
      // Treat everything else as P&L for cache-key purposes.
      return 'pl';
    },
    currentReportingUnit() {
      if (this.trendReportingUnit) {
        return this.trendReportingUnit;
      }
      const commentary = this.resolvedContext;
      return (
        commentary.reportingunit_detail?._id ||
        commentary.reportingunit ||
        commentary.reportingunitReadable ||
        commentary.reportingunit_detail?.code ||
        ''
      );
    },
    glIdentifier() {
      const commentary = this.resolvedContext || {};
      const detail = commentary.glcode_detail || {};

      return (
        detail._id ||
        commentary.glcodeId ||
        commentary.glcode ||
        commentary.glcode_internal ||
        commentary.gl_code ||
        detail.code ||
        detail.glcode ||
        commentary.client_code ||
        commentary.clcode ||
        commentary._id ||
        ''
      );
    },
    commentaryId() {
      const commentary = this.resolvedContext;
      return commentary._id || commentary.id || '';
    },
    contextKey() {
      const missingYear = this.isMissingValue(this.trendYear);
      const missingPeriod = this.isMissingValue(this.trendPeriod);
      const missingRu = this.isMissingValue(this.currentReportingUnit);

      const cycleType = this.currentCycleType;
      if (!this.glIdentifier || missingYear || missingPeriod || missingRu || this.isMissingValue(cycleType)) {
        return '';
      }

      const yearNumber = Number(this.trendYear);
      const yearKey = Number.isFinite(yearNumber) ? yearNumber : this.trendYear;
      return `${this.glIdentifier}__${yearKey}__${this.trendPeriod}__${this.currentReportingUnit}__${cycleType}__${this.contextReportingTypeKey}`;
    },
    logsContextKey() {
      if (!this.commentaryId) {
        return '';
      }
      return `${this.commentaryId}__${this.currentCycleType}`;
    },
    trendlineData() {
      return this.contextKey ? this.trendlineDataGetter(this.contextKey) : null;
    },
    trendlineLogs() {
      return this.logsContextKey ? this.trendlineLogsGetter(this.logsContextKey) : [];
    },
    currentError() {
      return this.contextKey ? this.trendlineErrorGetter(this.contextKey) : '';
    },
    currentLogsError() {
      return this.logsContextKey ? this.trendlineLogsErrorGetter(this.logsContextKey) : '';
    },
    isCurrentLoading() {
      return this.trendlineLoadingFromStore && this.trendlineActiveKey === this.contextKey;
    },
    isCurrentLogsLoading() {
      return this.trendlineLogsLoadingFlag && this.trendlineLogsActiveKey === this.logsContextKey;
    },
    hasData() {
      return Array.isArray(this.trendlineData) && this.trendlineData.length > 0;
    },
  },
  watch: {
    charttab(newVal) {
      if (newVal === 1 && this.isMounted) {
        this.ensureTrendlineResources();
      }
    },
    trendYear() {
      this.handleContextChange();
    },
    trendPeriod() {
      this.handleContextChange();
    },
    trendReportingUnit() {
      this.handleContextChange();
    },
    reportingType() {
      this.handleContextChange(true);
    },
    selectedcommentary: {
      handler() {
        this.handleContextChange(true);
      },
      deep: false,
    },
    glContext: {
      handler() {
        this.handleContextChange(true);
      },
      deep: false,
    },
    trendlineData(newData) {
      if (this.isActiveTab && Array.isArray(newData)) {
        this.renderChart(newData);
      } else if (!newData) {
        this.destroyChart();
      }
    },
  },
  methods: {
    ...mapActions('varianceanalysis', ['fetchTrendlineData', 'fetchTrendlineLogs']),
    handleContextChange(force = false) {
      if (!this.isMounted) {
        return;
      }
      if (this.isActiveTab) {
        this.ensureTrendlineResources(force);
      }
    },
    ensureTrendlineResources(force = false) {
      if (!this.isActiveTab) {
        return;
      }
      this.ensureTrendlineData(force);
      this.ensureTrendlineLogs(force);
    },
    async ensureTrendlineData(force = false) {
      if (!this.isActiveTab || !this.contextKey) {
        return;
      }
      
      // If currently fetching, mark for retry after completion
      if (this.isFetchingData) {
        this.pendingDataRefetch = true;
        return;
      }
      
      if (!force && (this.hasData || this.isCurrentLoading)) {
        if (this.hasData) {
          this.renderChart(this.trendlineData);
        }
        return;
      }

      const axiosInstance = this.axios || this.$axios || this.$http;
      if (!axiosInstance) {
        return;
      }

      const cycleType = this.currentCycleType;
      const normalizedReportingType = this.contextReportingTypeKey === 'bs' ? 'bs' : '';

      const yearNumber = Number(this.trendYear);
      const normalizedYear = Number.isFinite(yearNumber) ? yearNumber : '';
      const filter = {
        cycletype: cycleType,
        glcode: this.glIdentifier,
        year: normalizedYear,
        period: this.trendPeriod,
        reportingunit: this.currentReportingUnit,
      };

      if (normalizedReportingType) {
        filter.reporting_type = normalizedReportingType;
      }

      if (
        !filter.glcode ||
        this.isMissingValue(filter.year) ||
        this.isMissingValue(filter.period) ||
        this.isMissingValue(filter.reportingunit)
      ) {
        return;
      }

      // Set guard flag before making API call
      this.isFetchingData = true;
      
      try {
        await this.fetchTrendlineData({
          axios: axiosInstance,
          filter,
          contextKey: this.contextKey,
          successMessage: 'Trendline data ready',
        });
      } catch (error) {
        console.error('[CommentaryTrendlineTab]: fetchTrendlineData failed', error);
      } finally {
        // Always clear guard flag when done
        this.isFetchingData = false;
        
        // If context changed during fetch, retry with new context
        if (this.pendingDataRefetch) {
          this.pendingDataRefetch = false;
          this.$nextTick(() => {
            this.ensureTrendlineData(true); // Force refetch with new context
          });
        }
      }
    },
    async ensureTrendlineLogs(force = false) {
      if (!this.isActiveTab || !this.logsContextKey || !this.commentaryId) {
        return;
      }
      
      // If currently fetching, mark for retry after completion
      if (this.isFetchingLogs) {
        this.pendingLogsRefetch = true;
        return;
      }
      
      const existingLogs = this.trendlineLogs;
      // Prevent duplicate calls: skip if logs exist OR if already loading
      if (!force && ((Array.isArray(existingLogs) && existingLogs.length > 0) || this.isCurrentLogsLoading)) {
        return;
      }

      const axiosInstance = this.axios || this.$axios || this.$http;
      if (!axiosInstance) {
        return;
      }

      // Set guard flag before making API call
      this.isFetchingLogs = true;

      try {
        await this.fetchTrendlineLogs({
          axios: axiosInstance,
          contextKey: this.logsContextKey,
          commentaryId: this.commentaryId,
          cycleType: this.currentCycleType,
          successMessage: '',
        });
      } catch (error) {
        console.error('[CommentaryTrendlineTab]: fetchTrendlineLogs failed', error);
      } finally {
        // Always clear guard flag when done
        this.isFetchingLogs = false;
        
        // If context changed during fetch, retry with new context
        if (this.pendingLogsRefetch) {
          this.pendingLogsRefetch = false;
          this.$nextTick(() => {
            this.ensureTrendlineLogs(true); // Force refetch with new context
          });
        }
      }
    },
    renderChart(data) {
      if (!Array.isArray(data) || !data.length) {
        this.destroyChart();
        return;
      }

      this.$nextTick(() => {
        const canvas = typeof document !== 'undefined' ? document.getElementById(this.trendchartElementId) : null;
        if (!canvas) {
          return;
        }

        this.destroyChart();
        const { labels, datasets } = this.transformDataToChart(data);
        if (!datasets.length) {
          return;
        }

        const chartConfig = {
          type: 'line',
          data: {
            labels,
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index',
              intersect: false,
            },
            scales: {
              x: {
                stacked: false,
                grid: {
                  display: true,
                  drawOnChartArea: true,
                  drawTicks: true,
                  color: 'rgba(0, 0, 0, 0.1)',
                },
              },
              y: {
                stacked: false,
                beginAtZero: true,
                grid: {
                  display: true,
                  drawOnChartArea: true,
                  drawTicks: true,
                  color: 'rgba(0, 0, 0, 0.1)',
                },
              },
            },
            plugins: {
              legend: {
                position: 'top',
                labels: {
                  filter: (legendItem, chartData) => {
                    const dataset = chartData.datasets[legendItem.datasetIndex];
                    return dataset.type !== 'line';
                  },
                },
                onClick: (event, legendItem, legend) => {
                  const chartInstance = legend.chart;
                  const label = legendItem.text;
                  const isVisible = chartInstance.isDatasetVisible(legendItem.datasetIndex);
                  chartInstance.data.datasets.forEach((dataset, index) => {
                    if (dataset.label === label) {
                      if (isVisible) {
                        chartInstance.hide(index);
                      } else {
                        chartInstance.show(index);
                      }
                    }
                  });
                },
              },
              tooltip: {
                filter: (tooltipItem) => {
                  return tooltipItem.dataset.type === 'line' || tooltipItem.dataset.label !== this.primaryHeader;
                },
                callbacks: {
                  label: (ctx) => {
                    const value = ctx.parsed.y;
                    return `${ctx.dataset.label}: ${this.$nova.formatNumber(value)}`;
                  },
                },
              },
              title: {
                display: false,
                text: '',
              },
            },
          },
        };

        this.trendchart = new Chart(canvas, chartConfig);
      });
    },
    transformDataToChart(data) {
      let primaryHeader = this.currentCycleType;
      if (primaryHeader === 'Actual') {
        primaryHeader = this.actualLabel;
      } else if (primaryHeader === 'Proforma') {
        primaryHeader = this.proformaLabel;
      } else if (primaryHeader === 'Forecast') {
        primaryHeader = this.forecastLabel;
      }

      const proformaHeader = this.proformaLabel;
      const forecastHeader = this.currentCycleType === 'Actual'
        ? this.forecastLabel
        : `Previous ${this.forecastLabel}`;
      this.primaryHeader = primaryHeader;

      const colors = {
        [primaryHeader]: '#00C2C2',
        [proformaHeader]: '#8EA0B4',
        [forecastHeader]: '#4285F4',
      };

      const labels = [];
      
      // Initialize datasets with correct order
      const datasetMap = {
        [proformaHeader]: {
          label: proformaHeader,
          data: [],
          backgroundColor: colors[proformaHeader],
          borderColor: colors[proformaHeader],
          type: 'bar',
          order: 1,
        },
        [primaryHeader]: {
          label: primaryHeader,
          data: [],
          backgroundColor: colors[primaryHeader],
          borderColor: '#193541',
          type: 'bar',
          order: 2,
        },
        [forecastHeader]: {
          label: forecastHeader,
          data: [],
          backgroundColor: colors[forecastHeader],
          borderColor: colors[forecastHeader],
          type: 'bar',
          order: 3,
        }
      };

      // Iterate 1 to 12 for months to ensure full year display
      for (let i = 1; i <= 12; i++) {
        // Find data for this month (assuming period_detail.number is 1-12)
        const entry = data.find(d => Number(d?.period_detail?.number) === i);
        
        // Determine label
        let label = entry?.period_detail?.name;
        if (!label) {
             // Generate short month name if missing
             const date = new Date();
             date.setMonth(i - 1);
             label = date.toLocaleString('en-US', { month: 'short' });
        }
        labels.push(label);

        // Extract amounts or default to 0
        const amount1 = entry ? (Number(entry.amount1) || 0) : 0;
        const amount2 = entry ? (Number(entry.amount2) || 0) : 0;
        const amount3 = entry ? (Number(entry.amount3) || 0) : 0;

        datasetMap[primaryHeader].data.push(amount1);
        datasetMap[proformaHeader].data.push(amount2);
        datasetMap[forecastHeader].data.push(amount3);
      }

      const datasets = [];
      // Reorder to show Proforma, Actual (Center), Forecast
      const orderedHeaders = [proformaHeader, primaryHeader, forecastHeader];

      orderedHeaders.forEach((header) => {
        const dataset = datasetMap[header];
        if (dataset) {
          datasets.push({ ...dataset });
          if (dataset.label === primaryHeader) {
            datasets.push({
              ...dataset,
              type: 'line',
              fill: false,
              tension: 0.25,
              borderWidth: 2,
              pointRadius: 3,
              pointBackgroundColor: '#193541',
              pointBorderColor: '#193541',
              order: 1,
            });
          }
        }
      });

      return { labels, datasets };
    },
    destroyChart() {
      if (this.trendchart && typeof this.trendchart.destroy === 'function') {
        this.trendchart.destroy();
      }
      this.trendchart = null;
    },
    isMissingValue(value) {
      return value === null || value === undefined || value === '';
    },
  },
  mounted() {
    this.isMounted = true;
    if (this.isActiveTab) {
      this.ensureTrendlineResources();
    }
  },
  beforeDestroy() {
    // Clear guard flags
    this.isFetchingData = false;
    this.isFetchingLogs = false;
    this.pendingDataRefetch = false;
    this.pendingLogsRefetch = false;
    this.destroyChart();
    this.$store.commit('varianceanalysis/setTrendlineActiveKey', '');
    this.$store.commit('varianceanalysis/setTrendlineLogsActiveId', '');
    this.isMounted = false;
  },
};
</script>

<style scoped>
.trendline-chart-container {
  position: relative;
  min-height: 300px;
}

.trendline-chart-container__loader,
.trendline-chart-container__empty,
.trendline-chart-container__error {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.86);
  border-radius: 8px;
}

.trendline-chart-container__empty {
  color: #666;
  font-weight: 500;
}

.trendline-chart-container__error {
  font-weight: 600;
}
</style>
